import React, { useState, useCallback, useMemo, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import FunctionsIcon from '@mui/icons-material/Functions'
import RefreshIcon from '@mui/icons-material/Refresh'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ScienceIcon from '@mui/icons-material/Science'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import InsightsIcon from '@mui/icons-material/Insights'
import CloseIcon from '@mui/icons-material/Close'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import RoadRiskMapSettingsDialog from './RoadRiskMapSettingsDialog'
import RoadRiskMapFormulaDialog from './RoadRiskMapFormulaDialog'
import { downloadRoadRiskMapCsv } from '../../utils/road-risk-map-csv'
import {
  formatRiskTrendPct,
  getSegmentRiskTrendPct,
} from '../../utils/road-risk-map-trend'
import {
  RISK_MAP_TOP_LIST_SIZE_DEFAULT,
  RISK_MAP_DEMO_SEED_ENABLED,
  RISK_MAP_DEMO_SEED_COUNT,
  RISK_MAP_DEMO_SEED_MAX,
  RISK_MAP_NETWORK_MIN_ZOOM,
  formatRiskScore,
  resolveRiskMapTopListSize,
} from '../../shared/constants/risk-map'
import { MAP_ANALYSIS_TOOLBAR_WIDTH } from './MapYearFilterToolbar'
import {
  MAPLIBRE_CTRL_BTN_SIZE,
  maplibreGroupChildIconButtonSx,
} from '../../consts/map-floating-control-sx'
import {
  mapAnalysisActionBtnSx,
  mapAnalysisPanelHeaderSx,
  mapAnalysisPanelShellSx,
  mapAnalysisPanelTokens,
  mapAnalysisShowHideButtonSx,
  mapAnalysisTitleSx,
} from '../../consts/map-analysis-panel-sx'

const TAB_HEATMAP = 0
const TAB_RISK = 1

function featureCenter(feature) {
  const lon = Number(feature?.properties?.centerLon)
  const lat = Number(feature?.properties?.centerLat)
  if (Number.isFinite(lon) && Number.isFinite(lat)) {
    return [lon, lat]
  }
  const coords = []
  const walk = (node) => {
    if (!Array.isArray(node)) return
    if (typeof node[0] === 'number' && typeof node[1] === 'number') {
      coords.push([node[0], node[1]])
      return
    }
    node.forEach(walk)
  }
  walk(feature?.geometry?.coordinates)
  if (coords.length === 0) return null
  let sumLon = 0
  let sumLat = 0
  coords.forEach(([x, y]) => {
    sumLon += x
    sumLat += y
  })
  return [sumLon / coords.length, sumLat / coords.length]
}

function segmentLabel(properties) {
  const name = properties?.name?.trim()
  if (name) return name
  const ref = properties?.ref?.trim()
  if (ref) return ref
  return '—'
}

function TrendCell({ trendPct }) {
  if (trendPct == null || !Number.isFinite(trendPct)) {
    return (
      <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
        -
      </Typography>
    )
  }

  const isUp = trendPct > 0
  const isDown = trendPct < 0
  const color = isUp ? 'success.main' : isDown ? 'error.main' : 'text.secondary'

  return (
    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.25}>
      {isUp && <ArrowUpwardIcon sx={{ fontSize: 14, color }} />}
      {isDown && <ArrowDownwardIcon sx={{ fontSize: 14, color }} />}
      <Typography
        component="span"
        sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', color, whiteSpace: 'nowrap' }}
      >
        {formatRiskTrendPct(trendPct)}
      </Typography>
    </Stack>
  )
}

const cellSx = { py: 0.25, px: 0.75, fontSize: 11 }

/**
 * Square MapLibre-style toggle for the Spatial analysis panel (placed after year filter).
 */
export function RoadRiskMapToolbarToggle({
  open = false,
  onToggle,
  active = false,
  disabled = false,
}) {
  const theme = useTheme()
  const { _ } = useLingui()
  const floatSx = maplibreGroupChildIconButtonSx(theme)
  const tokens = mapAnalysisPanelTokens(theme)
  const isActive = open || active

  return (
    <Box
      className="maplibregl-ctrl maplibregl-ctrl-group"
      sx={{
        borderRadius: 'var(--radius-lg, 8px)',
        overflow: 'hidden',
        bgcolor: tokens.surfaceBg,
      }}
    >
      <Tooltip title={_(msg`Spatial analysis`)} placement="right">
        <span>
          <IconButton
            className="fred-map-toolbar-btn"
            size="small"
            onClick={() => onToggle?.()}
            disabled={disabled}
            aria-label={_(msg`Spatial analysis`)}
            aria-expanded={open}
            aria-pressed={isActive}
            sx={{
              ...floatSx,
              width: MAPLIBRE_CTRL_BTN_SIZE,
              height: MAPLIBRE_CTRL_BTN_SIZE,
              color: isActive ? tokens.accent : floatSx.color,
              bgcolor: isActive ? tokens.activeRowBg : floatSx.backgroundColor,
              '&:hover': {
                bgcolor: isActive ? tokens.activeRowBg : floatSx['&:hover']?.backgroundColor,
                color: tokens.accent,
              },
              '& .MuiSvgIcon-root': {
                fontSize: 18,
              },
            }}
          >
            <InsightsIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

/**
 * Analysis toolbox panel: Heatmap / Road risk (year filter lives in MapYearFilterToolbar).
 */
export default function RoadRiskMapToolbar({
  visible,
  settings,
  roadRiskMapData,
  previousScoresBySegmentId = null,
  loading,
  onRefresh,
  riskMapYear,
  onFocusRiskSegment,
  roadNetworkUnderlayEnabled = false,
  onRoadNetworkUnderlayEnabledChange,
  mapZoom = null,
  onDemoSeed,
  demoSeedLoading = false,
  heatmapVisible = false,
  onHeatmapVisibleChange,
  riskMapVisible = false,
  onRiskMapVisibleChange,
  analysisYearLocked = false,
  onCollapse,
}) {
  const theme = useTheme()
  const { _ } = useLingui()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [formulaOpen, setFormulaOpen] = useState(false)
  const [demoSeedCount, setDemoSeedCount] = useState(RISK_MAP_DEMO_SEED_COUNT)
  const [tab, setTab] = useState(TAB_RISK)

  const topListSize = resolveRiskMapTopListSize(settings?.topListSize ?? RISK_MAP_TOP_LIST_SIZE_DEFAULT)

  const underlayAllowed =
    Number.isFinite(Number(mapZoom)) && Number(mapZoom) >= RISK_MAP_NETWORK_MIN_ZOOM

  useEffect(() => {
    if (!underlayAllowed && roadNetworkUnderlayEnabled) {
      onRoadNetworkUnderlayEnabledChange?.(false)
    }
  }, [underlayAllowed, roadNetworkUnderlayEnabled, onRoadNetworkUnderlayEnabledChange])

  const handleDownload = useCallback(() => {
    downloadRoadRiskMapCsv(roadRiskMapData, previousScoresBySegmentId, riskMapYear)
  }, [previousScoresBySegmentId, riskMapYear, roadRiskMapData])

  const topFeatures = useMemo(() => {
    const features = roadRiskMapData?.features
    if (!Array.isArray(features) || features.length === 0) return []
    return [...features]
      .sort((a, b) => Number(b?.properties?.riskScore ?? 0) - Number(a?.properties?.riskScore ?? 0))
      .slice(0, topListSize)
  }, [roadRiskMapData, topListSize])

  const handleFocus = useCallback(
    (feature) => {
      onFocusRiskSegment?.(feature)
    },
    [onFocusRiskSegment]
  )

  if (!visible) return null

  const tokens = mapAnalysisPanelTokens(theme)
  const layerToggleDisabled = loading || riskMapYear == null || analysisYearLocked
  const showHideButtonSx = (isVisible) => mapAnalysisShowHideButtonSx(theme, isVisible)
  const actionBtnSx = mapAnalysisActionBtnSx(theme)
  const actionBtnActiveSx = (active) => mapAnalysisActionBtnSx(theme, { active })

  return (
    <>
      <Box
        sx={{
          ...mapAnalysisPanelShellSx(theme),
          width: MAP_ANALYSIS_TOOLBAR_WIDTH,
          maxHeight: 'min(520px, calc(100% - 80px))',
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'auto',
          flexShrink: 0,
        }}
      >
        <Box sx={mapAnalysisPanelHeaderSx(theme)}>
          <Typography variant="caption" sx={mapAnalysisTitleSx(theme)}>
            {riskMapYear != null ? (
              <Trans>Spatial analysis ({riskMapYear})</Trans>
            ) : (
              <Trans>Spatial analysis</Trans>
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip title={_(msg`Refresh analysis`)} placement="bottom">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onRefresh?.()}
                  disabled={loading}
                  aria-label={_(msg`Refresh analysis`)}
                  sx={actionBtnSx}
                >
                  {loading ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: tokens.accent }} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                !underlayAllowed
                  ? _(msg`Zoom in to show the road network underlay`)
                  : roadNetworkUnderlayEnabled
                    ? _(msg`Hide road network underlay`)
                    : _(msg`Show road network underlay`)
              }
              placement="bottom"
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => onRoadNetworkUnderlayEnabledChange?.(!roadNetworkUnderlayEnabled)}
                  disabled={!underlayAllowed}
                  aria-label={
                    roadNetworkUnderlayEnabled
                      ? _(msg`Hide road network underlay`)
                      : _(msg`Show road network underlay`)
                  }
                  aria-pressed={roadNetworkUnderlayEnabled}
                  sx={actionBtnActiveSx(roadNetworkUnderlayEnabled)}
                >
                  <AccountTreeIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            {RISK_MAP_DEMO_SEED_ENABLED && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                <TextField
                  type="number"
                  size="small"
                  value={demoSeedCount}
                  onChange={(e) => {
                    const next = Number(e.target.value)
                    if (!Number.isFinite(next)) {
                      setDemoSeedCount(RISK_MAP_DEMO_SEED_COUNT)
                      return
                    }
                    setDemoSeedCount(Math.min(RISK_MAP_DEMO_SEED_MAX, Math.max(1, Math.round(next))))
                  }}
                  inputProps={{
                    min: 1,
                    max: RISK_MAP_DEMO_SEED_MAX,
                    'aria-label': _(msg`TEMPORARY: demo crash count`),
                  }}
                  disabled={loading || demoSeedLoading}
                  sx={{
                    width: 56,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 'var(--radius-md, 6px)',
                      bgcolor: tokens.isLight ? tokens.headerBg : 'transparent',
                    },
                    '& .MuiInputBase-input': {
                      py: 0.5,
                      px: 0.75,
                      fontSize: 12,
                      textAlign: 'center',
                      color: tokens.textPrimary,
                    },
                  }}
                />
                <Tooltip
                  title={_(msg`TEMPORARY: seed validated Police crashes in current view`)}
                  placement="bottom"
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onDemoSeed?.(demoSeedCount)}
                      disabled={loading || demoSeedLoading || riskMapYear == null}
                      aria-label={_(msg`TEMPORARY: seed demo crashes`)}
                      sx={{
                        ...actionBtnSx,
                        color: theme.palette.warning.main,
                      }}
                    >
                      {demoSeedLoading ? (
                        <CircularProgress size={16} thickness={5} />
                      ) : (
                        <ScienceIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
            {typeof onCollapse === 'function' && (
              <Tooltip title={_(msg`Collapse`)} placement="bottom">
                <IconButton
                  size="small"
                  onClick={() => onCollapse()}
                  aria-label={_(msg`Collapse spatial analysis`)}
                  sx={actionBtnSx}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="fullWidth"
          sx={{
            minHeight: 36,
            borderBottom: `1px solid ${tokens.rowBorder}`,
            bgcolor: tokens.isLight ? tokens.headerBg : 'transparent',
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              fontSize: 'var(--font-sm, 12px)',
              textTransform: 'none',
              fontWeight: 600,
              color: tokens.textSecondary,
            },
            '& .Mui-selected': {
              color: `${tokens.accent} !important`,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: tokens.accent,
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab label={_(msg`Heatmap`)} id="analysis-tab-heatmap" aria-controls="analysis-panel-heatmap" />
          <Tab label={_(msg`Road risk`)} id="analysis-tab-risk" aria-controls="analysis-panel-risk" />
        </Tabs>

        {tab === TAB_HEATMAP && (
          <Box
            id="analysis-panel-heatmap"
            role="tabpanel"
            aria-labelledby="analysis-tab-heatmap"
            sx={{ px: 1.25, py: 1.5 }}
          >
            <Typography variant="body2" sx={{ mb: 1.5, color: tokens.textSecondary }}>
              <Trans>
                Density heatmap for all filtered crashes in the selected year.
              </Trans>
            </Typography>
            {analysisYearLocked && (
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: tokens.textSecondary }}>
                <Trans>Select a single year</Trans>
              </Typography>
            )}
            <Button
              fullWidth
              size="small"
              variant="contained"
              onClick={() => onHeatmapVisibleChange?.(!heatmapVisible)}
              disabled={layerToggleDisabled}
              sx={showHideButtonSx(heatmapVisible)}
            >
              {heatmapVisible ? <Trans>Hide heatmap</Trans> : <Trans>Show heatmap</Trans>}
            </Button>
          </Box>
        )}

        {tab === TAB_RISK && (
          <Box
            id="analysis-panel-risk"
            role="tabpanel"
            aria-labelledby="analysis-tab-risk"
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 1.25, pt: 1.25, pb: 0.75 }}>
              {analysisYearLocked && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: tokens.textSecondary }}>
                  <Trans>Select a single year</Trans>
                </Typography>
              )}
              <Button
                fullWidth
                size="small"
                variant="contained"
                onClick={() => onRiskMapVisibleChange?.(!riskMapVisible)}
                disabled={layerToggleDisabled}
                sx={showHideButtonSx(riskMapVisible)}
              >
                {riskMapVisible ? <Trans>Hide road risk</Trans> : <Trans>Show road risk</Trans>}
              </Button>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.5,
                px: 1.25,
                pt: 0.5,
                pb: 0.5,
              }}
            >
              <Typography variant="caption" sx={mapAnalysisTitleSx(theme)}>
                <Trans>Top {topListSize}</Trans>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Tooltip title={_(msg`Risk score formula`)} placement="bottom">
                  <IconButton
                    size="small"
                    onClick={() => setFormulaOpen(true)}
                    aria-label={_(msg`Risk score formula`)}
                    sx={actionBtnSx}
                  >
                    <FunctionsIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={_(msg`Risk map settings`)} placement="bottom">
                  <IconButton
                    size="small"
                    onClick={() => setSettingsOpen(true)}
                    aria-label={_(msg`Risk map settings`)}
                    sx={actionBtnSx}
                  >
                    <SettingsIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={_(msg`Download risk map CSV`)} placement="bottom">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleDownload}
                      disabled={!roadRiskMapData?.features?.length}
                      aria-label={_(msg`Download risk map CSV`)}
                      sx={actionBtnSx}
                    >
                      <FileDownloadIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', pb: 0.5 }}>
              {topFeatures.length === 0 ? (
                <Typography variant="caption" sx={{ display: 'block', px: 1.25, pb: 1, color: tokens.textSecondary }}>
                  <Trans>No segments</Trans>
                </Typography>
              ) : (
                <Table size="small" stickyHeader aria-label={_(msg`Top risk segments`)}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...cellSx, py: 0.5, color: tokens.textPrimary, fontWeight: 700, bgcolor: tokens.headerBg }}><Trans>ID</Trans></TableCell>
                      <TableCell sx={{ ...cellSx, py: 0.5, color: tokens.textPrimary, fontWeight: 700, bgcolor: tokens.headerBg }}><Trans>Name</Trans></TableCell>
                      <TableCell align="right" sx={{ ...cellSx, py: 0.5, color: tokens.textPrimary, fontWeight: 700, bgcolor: tokens.headerBg }}>
                        <Trans>Risk / km</Trans>
                      </TableCell>
                      <TableCell align="right" sx={{ ...cellSx, py: 0.5, color: tokens.textPrimary, fontWeight: 700, bgcolor: tokens.headerBg }}>
                        <Trans>Trend</Trans>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5, px: 0.5, width: 36, bgcolor: tokens.headerBg }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topFeatures.map((feature) => {
                      const p = feature.properties || {}
                      const id = p.segmentId
                      const score = Number(p.riskScore ?? 0)
                      const trendPct = getSegmentRiskTrendPct(previousScoresBySegmentId, id, score)
                      const canFocus = Boolean(featureCenter(feature))
                      return (
                        <TableRow
                          key={id != null ? `seg-${id}` : feature.id}
                          hover
                          sx={{
                            '&:hover': { bgcolor: tokens.rowHoverBg },
                          }}
                        >
                          <TableCell
                            sx={{
                              ...cellSx,
                              fontFamily: 'monospace',
                              whiteSpace: 'nowrap',
                              color: tokens.textPrimary,
                            }}
                          >
                            {id ?? '—'}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...cellSx,
                              maxWidth: 90,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: tokens.textPrimary,
                            }}
                            title={segmentLabel(p)}
                          >
                            {segmentLabel(p)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums', color: tokens.textPrimary }}
                          >
                            {formatRiskScore(score)}
                          </TableCell>
                          <TableCell align="right" sx={cellSx}>
                            <TrendCell trendPct={trendPct} />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0, px: 0.25 }}>
                            <Tooltip title={_(msg`Center map on segment`)} placement="right">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!canFocus}
                                  onClick={() => handleFocus(feature)}
                                  aria-label={_(msg`Center map on segment`)}
                                  sx={{ ...actionBtnSx, width: 28, height: 28, minWidth: 28 }}
                                >
                                  <CenterFocusStrongIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Box>
        )}
      </Box>
      <RoadRiskMapSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
      />
      <RoadRiskMapFormulaDialog
        open={formulaOpen}
        onClose={() => setFormulaOpen(false)}
        settings={settings}
      />
    </>
  )
}
